import { ChangeDetectionStrategy, Component, inject, Input, OnChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';

import { FilePageLinkService, FilePageLinkSource, FilePageLinks } from '../../file-page-link.service';

/** Supported visual treatments for a file/page scope. */
export type FilePageScopeMode = 'link' | 'card' | 'inline';

interface FilePageScopeView {
  links: FilePageLinks;
  fileText: string;
  pageText: string;
  pageLabel: 'Page' | 'Pages';
  fileAriaLabel: string;
  pageAriaLabel: string;
  pageTooltip: string;
}

/**
 * Shared presentation for a file and its page scope.
 *
 * The component centralizes deep links, tooltips, accessible names, focus treatment, and Material
 * token-based styling so dialog cards and compact table/inline scopes behave consistently.
 */
@Component({
  selector: 'app-file-page-scope',
  imports: [MatIconModule, MatTooltipModule, RouterLink],
  host: {
    '[class.file-page-scope-host--inline]': "mode === 'inline'",
  },
  templateUrl: './file-page-scope.component.html',
  styleUrls: ['./file-page-scope.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePageScopeComponent implements OnChanges {
  private filePageLinkService = inject(FilePageLinkService);

  /** Neutral file/page data used to resolve routes, tooltips, and accessible names. */
  @Input({ required: true }) source: FilePageLinkSource | null = null;

  /** Visual treatment appropriate to the surrounding UI. */
  @Input() mode: FilePageScopeMode = 'link';

  /** Optional filename or other friendlier value in place of the file identifier. */
  @Input() fileDisplay = '';

  /** Optional page range/list in place of the source's single routing page. */
  @Input() pageDisplay = '';

  /** Optional page title appended in compact link mode. */
  @Input() pageTitle = '';

  view: FilePageScopeView = this.buildView();

  ngOnChanges(): void {
    this.view = this.buildView();
  }

  private buildView(): FilePageScopeView {
    const links = this.filePageLinkService.buildLinks(this.source);
    const fileText = this.fileDisplay || links.fileIdentifier;
    const pageText = this.pageDisplay || links.pageNumber;
    const pageLabel = this.isMultiplePageDisplay(pageText) ? 'Pages' : 'Page';
    const visibleFileLabel = this.mode === 'link' ? `File ${fileText}` : fileText;
    const visiblePageLabel = this.mode === 'link' ? `${pageLabel} ${pageText}` : pageText;
    const pageTooltip =
      this.pageDisplay && this.pageDisplay !== links.pageNumber ? `Open page ${links.pageNumber}` : links.pageTooltip;

    return {
      links,
      fileText,
      pageText,
      pageLabel,
      fileAriaLabel: fileText ? FilePageLinkService.accessibleName(visibleFileLabel, links.fileTooltip) : '',
      pageAriaLabel: pageText ? FilePageLinkService.accessibleName(visiblePageLabel, pageTooltip) : '',
      pageTooltip,
    };
  }

  private isMultiplePageDisplay(pageText: string): boolean {
    return pageText.includes(',') || pageText.includes('-');
  }
}
